// Elintra — AEO Analytics Page | v3.2 | UAG v3.0.0
// Renamed from AEOAnalyticsPage → AnalyticsPage (canonical v3.2 name).
// Additions: x402_payments line on chart, algoSettledUsdt stat card,
// x402 LIVE badge strip. Reads x402 state directly from useAppStore.

import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Area, AreaChart, Bar, BarChart,
  CartesianGrid, Cell, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertCircle, BarChart3, Coins, Download, FileJson, TrendingUp, Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorBanner } from '@/components/shared/ErrorBanner';
import { SkeletonCard } from '@/components/shared/SkeletonCard';
import { useAppStore } from '@/store/appStore';
import { PROTOCOL_COLORS, COLORS } from '@/lib/utils';
import { AEO_QUERY_TABLE } from '@/lib/fixtures';
import type { AgentQuery, ChartDataPoint } from '@/types/app';

const PROTOCOL_TRAFFIC_KEYS = [
  { key: 'A2A'  as const, color: PROTOCOL_COLORS.A2A  },
  { key: 'ACP'  as const, color: PROTOCOL_COLORS.ACP  },
  { key: 'UCP'  as const, color: PROTOCOL_COLORS.UCP  },
  { key: 'NLIP' as const, color: PROTOCOL_COLORS.NLIP },
];

const RECOMMENDATIONS = [
  {
    icon: TrendingUp, iconColor: 'text-emerald-400', type: 'add-price',
    title: 'Add structured pricing to 3 products',
    desc: 'Agents reject unpriced products 4× more often. Adding price metadata increases match rate significantly.',
  },
  {
    icon: FileJson, iconColor: 'text-blue-400', type: 'schema',
    title: 'Complete ACP schema for video presets',
    desc: '"cinematic video preset pack" has 420 monthly queries but 0% match rate due to missing ACP category tags.',
  },
  {
    icon: AlertCircle, iconColor: 'text-amber-400', type: 'nlip',
    title: 'Improve NLIP intent coverage',
    desc: 'Add natural-language descriptions to improve agent intent matching across your Gumroad catalog.',
  },
] as const;

export const AnalyticsPage = ({
  agentQueries, chartData, aeoScore, isLoading, error,
}: {
  agentQueries: AgentQuery[];
  chartData:    ChartDataPoint[];
  aeoScore:     number;
  isLoading:    boolean;
  error:        string | null;
}) => {
  // v3.0.0: read x402 counters directly from store (no prop drilling needed)
  const algoSettledUsdt  = useAppStore((s) => s.algoSettledUsdt);

  const protocolBarData = useMemo(() =>
    ['A2A', 'ACP', 'UCP', 'NLIP'].map((proto) => ({
      name:  proto,
      value: agentQueries.filter((q) => q.protocol === proto).length,
    })),
  [agentQueries]);

  const aeoColor =
    aeoScore >= 80 ? '#10B981' :
    aeoScore >= 60 ? '#F59E0B' : '#EF4444';

  const aeoVariant =
    aeoScore >= 80 ? 'success' as const :
    aeoScore >= 60 ? 'warning' as const : 'danger' as const;

  // v3.0.0: total x402 payments derived from chart (live count from store)
  const totalX402FromChart = useMemo(() =>
    chartData.reduce((sum, d) => sum + (d.x402_payments ?? 0), 0),
  [chartData]);

  if (isLoading && chartData.length === 0) {
    return (
      <div className="max-w-6xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SkeletonCard rows={4} />
          <SkeletonCard rows={4} className="md:col-span-2" />
        </div>
        <SkeletonCard rows={6} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      <AnimatePresence>
        {error && <ErrorBanner message={error} />}
      </AnimatePresence>

      {/* ── v3.0.0: x402 Live Badge Strip ──────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg
                        bg-emerald-500/8 border border-emerald-500/20">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <Zap className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-emerald-400 font-mono">x402 LIVE</span>
          <span className="text-xs text-zinc-600">·</span>
          <code className="text-xs font-mono text-zinc-400">elintra.algo</code>
        </div>
        <span className="text-xs text-zinc-600">
          {totalX402FromChart} payments in window · ${algoSettledUsdt} USDT settled
        </span>
      </div>

      {/* ── AEO Score + Recommendations ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* AEO Score Dial */}
        <Card className="p-6 flex flex-col items-center justify-center text-center gap-4">
          <div className="relative w-32 h-32">
            <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
              <circle cx="60" cy="60" r="50" fill="none"
                stroke="#27272A" strokeWidth="10" />
              <motion.circle cx="60" cy="60" r="50" fill="none"
                stroke={aeoColor} strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 50}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 50 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 50 * (1 - aeoScore / 100) }}
                transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold font-mono">{aeoScore}</span>
              <span className="text-xs text-zinc-500">AEO Score</span>
            </div>
          </div>
          <Badge variant={aeoVariant}>
            {aeoScore >= 80 ? 'Strong' : aeoScore >= 60 ? 'Moderate' : 'Needs Work'}
          </Badge>
          <h3 className="font-bold text-lg mb-1">Store AEO Health</h3>
          <p className="text-zinc-400 text-sm">
            {aeoScore >= 80
              ? 'Strong agent discoverability'
              : aeoScore >= 60
              ? 'Room for optimization'
              : 'Needs immediate attention'}
          </p>
          <div className="grid grid-cols-3 gap-3 mt-2 w-full text-center">
            {[
              { label: 'Purchases', value: agentQueries.filter((q) => q.outcome === 'PURCHASED').length, color: 'text-emerald-400' },
              { label: 'Matched',   value: agentQueries.filter((q) => q.outcome === 'MATCHED').length,   color: 'text-blue-400'    },
              { label: 'Rejected',  value: agentQueries.filter((q) => q.outcome === 'REJECTED').length,  color: 'text-red-400'     },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <p className={`text-lg font-bold ${color}`}>{value}</p>
                <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Recommendations */}
        <Card className="p-6 md:col-span-2">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4 text-amber-400" />
            AEO Recommendations
          </h3>
          <div className="space-y-4">
            {RECOMMENDATIONS.map((rec) => (
              <div key={rec.type}
                className="flex items-start gap-4 p-4 rounded-lg bg-zinc-900/60 border
                           border-zinc-800/60 hover:border-zinc-700 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                  <rec.icon className={`w-4 h-4 ${rec.iconColor}`} />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-sm mb-1">{rec.title}</p>
                  <p className="text-zinc-400 text-xs leading-relaxed">{rec.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── v3.0.0: Constitutional Commerce Yield Stats ─────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          {
            icon: BarChart3, color: 'text-violet-400',
            label: 'Total Queries (30d)',
            value: chartData.reduce((s, d) => s + d.queries, 0).toLocaleString(),
          },
          {
            icon: TrendingUp, color: 'text-blue-400',
            label: 'Conversions (30d)',
            value: chartData.reduce((s, d) => s + d.conversions, 0).toLocaleString(),
          },
          {
            icon: Zap, color: 'text-emerald-400',
            label: 'x402 Payments (30d)',
            value: totalX402FromChart.toLocaleString(),
          },
          {
            icon: Coins, color: 'text-violet-400',
            label: 'USDT via Algorand',
            value: `$${algoSettledUsdt}`,
          },
        ].map(({ icon: Icon, color, label, value }) => (
          <Card key={label} className="p-5">
            <Icon className={`w-4 h-4 mb-2 ${color}`} />
            <p className="text-xs text-zinc-500 mb-1">{label}</p>
            <p className="text-xl font-bold font-mono">{value}</p>
          </Card>
        ))}
      </div>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Protocol Bar Chart */}
        <Card className="p-6">
          <h3 className="font-semibold mb-4 text-sm">Protocol Traffic</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={protocolBarData} barSize={32}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: '#A1A1AA', fontSize: 12 }}
                axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }}
                axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
                labelStyle={{ color: '#FAFAFA' }}
                itemStyle={{ color: '#A1A1AA' }}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {protocolBarData.map((entry) => (
                  <Cell key={entry.name} fill={PROTOCOL_COLORS[entry.name] ?? `#${COLORS.primary}`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Protocol Volume Area Chart */}
        <Card className="p-6 md:col-span-2">
          <h3 className="font-semibold mb-4 text-sm">Protocol Volume — 30 Days</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <defs>
                {PROTOCOL_TRAFFIC_KEYS.map(({ key, color }) => (
                  <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0}   />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 11 }}
                axisLine={false} tickLine={false} interval={6} />
              <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }}
                axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
                labelStyle={{ color: '#FAFAFA', marginBottom: '4px' }}
                itemStyle={{ color: '#A1A1AA' }}
              />
              {PROTOCOL_TRAFFIC_KEYS.map(({ key, color }) => (
                <Area key={key} type="monotone" dataKey={key}
                  stroke={color} strokeWidth={2}
                  fill={`url(#grad-${key})`} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-4 mt-3">
            {PROTOCOL_TRAFFIC_KEYS.map(({ key, color }) => (
              <div key={key} className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-xs text-zinc-400">{key}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* ── v3.0.0: x402 Payments Line Chart ──────────────────────────────── */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-emerald-400" />
            x402 Payments vs Conversions — 30 Days
          </h3>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-blue-400 inline-block rounded" />
              <span className="text-xs text-zinc-400">Conversions</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 border-t-2 border-dashed border-emerald-400 inline-block" />
              <span className="text-xs text-zinc-400">x402 Payments</span>
            </div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: '#A1A1AA', fontSize: 11 }}
              axisLine={false} tickLine={false} interval={6} />
            <YAxis tick={{ fill: '#A1A1AA', fontSize: 11 }}
              axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#18181B', border: '1px solid #27272A', borderRadius: '8px' }}
              labelStyle={{ color: '#FAFAFA', marginBottom: '4px' }}
              itemStyle={{ color: '#A1A1AA' }}
            />
            <Line
              type="monotone" dataKey="conversions"
              stroke="#3B82F6" strokeWidth={2} dot={false}
              name="Conversions"
            />
            <Line
              type="monotone" dataKey="x402_payments"
              stroke="#10B981" strokeWidth={2} dot={false}
              strokeDasharray="6 3"
              name="x402 Payments"
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* ── Query Intelligence Table ─────────────────────────────────────── */}
      <Card>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <h3 className="font-semibold text-sm">Query Intelligence</h3>
          <Button variant="outline" size="sm">
            <Download className="w-3.5 h-3.5 mr-1.5" />Export CSV
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                {['Query Intent', 'Volume', 'Match %', 'Conv Rate', 'Revenue', 'Trend'].map((h) => (
                  <th key={h} className={`px-5 py-3 font-medium ${h === 'Trend' ? 'text-right' : 'text-left'}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {AEO_QUERY_TABLE.map((row) => (
                <tr key={row.q} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-5 py-3 font-medium text-zinc-200">{row.q}</td>
                  <td className="px-5 py-3 font-mono text-zinc-300">{row.v.toLocaleString()}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-zinc-800 rounded-full h-1.5 w-16">
                        <div className="h-1.5 rounded-full bg-violet-500"
                          style={{ width: `${row.m}%` }} />
                      </div>
                      <span className="font-mono text-zinc-300 text-xs">{row.m}%</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-zinc-300">{row.c}%</td>
                  <td className="px-5 py-3 font-mono text-emerald-400">${row.r}</td>
                  <td className="px-5 py-3 text-right">
                    <Badge variant={
                      row.trend === 'up'   ? 'success' :
                      row.trend === 'down' ? 'danger'  : 'default'
                    }>
                      {row.trend === 'up' ? '↑ Up' : row.trend === 'down' ? '↓ Down' : '→ Flat'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
};
